import Accordion from 'LuxKit/Accordion'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import React from 'react'

export default function TravellerBookingAFlight() {
  return (
    <Group direction="vertical" gap={8}>
      <Heading variant="heading4">Booking a flight</Heading>
      <Accordion size="large" heading="How do I book a flight?">
        <BodyText variant="large">
          Booking a flight through Business Traveller is a straightforward process. Here's how:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Search for Flights:</BodyText>
              <BodyText variant="large"> Begin by navigating to the Luxury Escapes homepage and searching for flights. Enter your preferred departure and arrival airports, as well as your travel dates.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Select Flight Options:</BodyText>
              <BodyText variant="large"> After entering your search criteria, you will be presented with a list of available flight options. Review the flight times and select your preferred departure and return flights.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Proceed to Checkout:</BodyText>
              <BodyText variant="large"> Once you've selected your preferred flight times, proceed to checkout.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Request Approval (if required):</BodyText>
              <BodyText variant="large"> If necessary, submit a request for approval. Follow the prompts to provide any required information and await approval from relevant stakeholders within your company.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Review Details:</BodyText>
              <BodyText variant="large"> Before finalising your booking, review the details to ensure they match your passport or ID. Double-check the departure and arrival airports, travel dates, and passenger information.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Complete Payment:</BodyText>
              <BodyText variant="large"> Proceed to pay for your flight booking. Follow the prompts to enter your payment details and confirm the transaction.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Receive Booking Confirmation:</BodyText>
              <BodyText variant="large"> After completing the payment process, you will receive a flight booking confirmation shortly via email. This confirmation will include details of your flight itinerary and any other relevant information.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
      <Accordion size="large" heading="How do I change or cancel my flight?">
        <BodyText variant="large">
          Depending on the terms and conditions of the airfare booked, the fare may be refundable or refundable less a penalty. Even for non-refundable tickets, you are still able to get any unused taxes back. All refund requests must be made at least 24 hours prior to departure.
        </BodyText>
        <br />
        <BodyText variant="large">
          Refunds can take approximately 12 weeks to come back from the airline and will be processed back to the same method of payment. To check any fare terms and conditions, please contact our support team.
        </BodyText>
      </Accordion>
      <Accordion size="large" heading="How do I know if my company has set up a travel policy for flights?">
        <BodyText variant="large">
          You can determine if your company has set up a travel policy for flights through the following:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Communication from Company:</BodyText>
              <BodyText variant="large"> Your company should communicate any travel policies, including the approvals process, to you directly. Check for any emails, memos or documents provided by your company's HR or People & Culture department, travel coordinator, or management team regarding travel policies and procedures.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Checkout Notification:</BodyText>
              <BodyText variant="large"> When you proceed to the checkout page to book a flight, you will be notified if an approval request is required before completing the booking. This notification serves as an indication that your company has implemented a travel policy for flights, which includes an approvals process and reason for travel.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
      <Accordion size="large" heading="There are no flights within my budget. What should I do?">
        <BodyText variant="large">
          If you are unable to find flights within your company's designated budget, here are some steps you can take:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Notify Your Administrator or Manager:</BodyText>
              <BodyText variant="large"> Inform your Administrator or manager about the situation. They may be able to provide guidance or assistance in finding alternative options.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Discuss Policy Breach:</BodyText>
              <BodyText variant="large"> Depending on your company's decision-making process and policies, you may discuss the possibility of breaching the policy in this particular instance. Your Administrator or manager can advise you on the next steps.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Policy Update:</BodyText>
              <BodyText variant="large"> Alternatively, if there are consistently no flights within the budget, your company may consider updating its travel policy to better accommodate the current market conditions or your specific travel needs.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
      <Accordion size="large" heading="How come I need to request approval for my flight?">
        <BodyText variant="large">
          Your company has opted in for our Travel Approval Process feature. There may be a few reason why a approval process is triggered.
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Approval for All Flights:</BodyText>
              <BodyText variant="large"> As per your company's policy, all flights may require approval before booking. This ensures that travel plans are reviewed and authorised by relevant stakeholders, maintaining oversight and control over corporate travel expenses.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Budget Adherence:</BodyText>
              <BodyText variant="large"> Flights exceeding the allocated budget may require approval prior to booking. This step ensures that travel expenses remain within budgetary constraints and allows for consideration of exceptions or adjustments when necessary.</BodyText>
            </li>
          </Group>
        </ol>
        <BodyText variant="large">
          By implementing a Travel Approval Process, your company aims to promote responsible spending, enhance budget management, and ensure alignment with corporate travel policies.
        </BodyText>
      </Accordion>
    </Group>
  )
}
